import { DOMAIN_PATH_LOCALES, LOCALE_PATH_SEGMENTS } from '~/config/constants';

export const ANALYTICS_MASK = '****';

const LOCALE_PATH_PREFIXES = new Set(
  Object.values(DOMAIN_PATH_LOCALES)
    .flat()
    .map(locale => LOCALE_PATH_SEGMENTS[locale] ?? locale)
);

const PAYMENT_SEGMENTS = ['betala', 'pay'];
const IMPERSONATE_SEGMENTS = ['impersonate'];
const MEMBER_SEGMENTS = ['medlem', 'member'];
const MY_ACCOUNT_SEGMENTS = ['mitt-konto', 'my-account'];
const CHECKOUT_SEGMENTS = ['checkout'];
const TRANSFER_SEGMENTS = [
  'overfor-fullmakt-kopratt',
  'transfer-attorney-right-to-buy',
  'overfor-fullmakt-hamta',
  'transfer-attorney-pickup',
  'overfor-fullmakt',
  'transfer-pickup-right',
  'overfor-kopratt',
  'transfer-purchase-right'
];
const BOOK_PICKUP_APPOINTMENT_SEGMENTS = [
  'boka_tid_upphamtning',
  'book_pickup_appointment'
];
const NOTIFICATION_UNSUBSCRIBE_SEGMENTS = ['unsubscribe'];
const CMS_PREVIEW_SEGMENTS = ['preview'];
const LOGOUT_SEGMENTS = ['logga-ut', 'logout'];

const PIXEL_BLOCKED_ROOT_SEGMENTS = [
  ...PAYMENT_SEGMENTS,
  ...IMPERSONATE_SEGMENTS,
  ...MEMBER_SEGMENTS,
  ...MY_ACCOUNT_SEGMENTS,
  ...CHECKOUT_SEGMENTS,
  ...TRANSFER_SEGMENTS,
  ...BOOK_PICKUP_APPOINTMENT_SEGMENTS,
  ...NOTIFICATION_UNSUBSCRIBE_SEGMENTS,
  ...CMS_PREVIEW_SEGMENTS,
  ...LOGOUT_SEGMENTS
];

const PIXEL_TRACKED_SUBPATHS: [root: string, step: string][] = [
  ['checkout', 'tack'],
  ['checkout', 'thanks']
];

const PIXEL_BLOCKED_WHEN_NESTED_ROOTS = [
  'vagnparksvardering',
  'fleet-valuation'
];

const SELL_ROOT_SEGMENTS = ['salja-bil', 'sell'];

const SELL_PROPOSAL_SEGMENTS = ['forslag', 'proposal'];
const SELL_RESERVATION_PRICE_SEGMENTS = [
  'reservationspris',
  'reservation-price'
];
const SELL_OWNER_VERIFICATION_SEGMENTS = [
  'agarverifiering',
  'owner-verification'
];
const SELL_FIND_FACILITY_SEGMENTS = ['hitta-anlaggning', 'find-facility'];
const SELL_BOOK_TIME_SEGMENTS = ['boka-tid', 'book-time'];
const SELL_AGREEMENT_SIGNED_SEGMENTS = ['signering-klar', 'agreement-signed'];
const SELL_TERMS_SEGMENTS = ['villkor', 'termsconditions'];

const SELL_BLOCKED_STEP_SEGMENTS = [
  ...SELL_PROPOSAL_SEGMENTS,
  ...SELL_RESERVATION_PRICE_SEGMENTS,
  ...SELL_OWNER_VERIFICATION_SEGMENTS,
  ...SELL_FIND_FACILITY_SEGMENTS,
  ...SELL_BOOK_TIME_SEGMENTS,
  ...SELL_AGREEMENT_SIGNED_SEGMENTS,
  ...SELL_TERMS_SEGMENTS
];

export const SENSITIVE_PATH_PREFIXES = [
  ...PAYMENT_SEGMENTS,
  ...IMPERSONATE_SEGMENTS
];

export const SENSITIVE_QUERY_KEYS = [
  'r',
  'token',
  'hash',
  'member_id',
  'jwt',
  'accessHash'
];

export const EMBEDDED_URL_QUERY_KEYS = ['ref'];

export const stripQueryFromEmbeddedUrl = (value: string): string => {
  let decoded = value;
  try {
    decoded = decodeURIComponent(value);
  } catch {}

  return decoded.split(/[?#]/)[0];
};

const OPAQUE_ID_SEGMENT =
  /^(?:[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}|[0-9a-fA-F]{16,}|(?=.*\d)[A-Za-z0-9_-]{5,})$/;

export const isOpaqueIdSegment = (segment: string): boolean =>
  OPAQUE_ID_SEGMENT.test(segment);

const toRouteSegments = (pathname: string): string[] => {
  let decoded = pathname;
  try {
    decoded = decodeURIComponent(pathname);
  } catch {}

  const segments = decoded.split('/').filter(Boolean);

  return LOCALE_PATH_PREFIXES.has(segments[0]) ? segments.slice(1) : segments;
};

export const isSensitivePathname = (pathname = ''): boolean => {
  const [root, ...rest] = toRouteSegments(pathname);

  if (!root) {
    return false;
  }

  if (PIXEL_BLOCKED_ROOT_SEGMENTS.includes(root)) {
    return !PIXEL_TRACKED_SUBPATHS.some(
      ([trackedRoot, trackedStep]) =>
        root === trackedRoot && rest[0] === trackedStep
    );
  }

  if (PIXEL_BLOCKED_WHEN_NESTED_ROOTS.includes(root)) {
    return rest.length > 0;
  }

  if (SELL_ROOT_SEGMENTS.includes(root)) {
    return rest.some(segment => SELL_BLOCKED_STEP_SEGMENTS.includes(segment));
  }

  return false;
};
